import { Component, OnDestroy } from '@angular/core';
import { BaseComponent } from '../../shared/base/base.component';
import * as L from 'leaflet';
import { BaseService } from '../../../providers/base.service';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { LocateControl } from "leaflet.locatecontrol";
import { addCenterControl } from '../map-center-control';
import { addSearchControl } from '../map-search-control';

// Where a dedicate prospect marks their site: the pin stays on the map centre,
// and the point goes to api/dedicate_location/submit. The page needs no login.
// It is opened from the link that SEND_LOCATION_LINK on a dedicate lead texts
// them, whose token leads back to that issue, or without one, in which case it
// also asks who is answering and the API files a new lead.
// Unlike the wireless map it never asks for the coverage of the point: staff see
// that on the issue, the visitor only that the point was received.
@Component({
  selector: 'app-dedicate-location',
  templateUrl: './dedicate-location.component.html',
  styleUrls: ['./dedicate-location.component.scss']
})
export class DedicateLocationComponent extends BaseComponent implements OnDestroy {
  map: L.Map;
  marker: L.Marker;
  linkToken: string; // from the link; kept only while the API says it leads to an issue
  ready = false; // the token has been checked, so it is known whether to ask for the contact
  floors: number;
  address = '';
  contact = { person_type_fa: 'حقیقی', name: '', family: '', company_name: '', identification_no: '', mobile: '', tel: '', email: '' };
  error: string;
  done = false;

  constructor(
    public service: BaseService,
    public translator: TranslateService,
    public cookieService: CookieService,
    public titleService: Title,
    public route: ActivatedRoute,) {
    super(service, translator, cookieService, titleService);
  }

  async ngOnInit() {
    await super.ngOnInit();
    this.translator.get('DEDICATE_LOCATION').subscribe(title => this.pageTitle = title);
    this.linkToken = this.route.snapshot.paramMap.get('token');
    if (this.linkToken) {
      try {
        const result = await this.service.apiCall('api/dedicate_location/check', { token: this.linkToken }).toPromise();
        if (!result.valid) {
          this.linkToken = null;
        }
      } catch (error) {
        this.linkToken = null; // the form then asks for the contact, and nothing is lost
      }
    }
    this.ready = true;
  }

  ngOnDestroy(): void {
    this.map = null;
    this.marker = null;
  }

  mapOptions = {
    layers: [L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; Petiak System'
    })],
    zoom: 16,
    center: L.latLng([35.77880664304775, 51.43510222434998])
  };

  async onMapReady(map: L.Map) {
    map.invalidateSize();
    this.map = map;
    // Nothing has loaded the translations yet on a page outside the layout, and
    // the controls below take their texts with instant()
    await this.translator.get('SEARCH').toPromise();
    if (!this.map) {
      return; // destroyed meanwhile
    }

    new LocateControl({
      position: 'bottomleft',
      keepCurrentZoomLevel: true,
      strings: {
        title: this.translator.instant('MAP_LOCATOR_TITLE')
      }
    }).addTo(this.map);

    addSearchControl(map, this.translator, this.service);
    addCenterControl(map, this.translator);

    this.marker = L.marker(this.map.getCenter(), {
      draggable: false,
      icon: L.icon({
        iconUrl: `assets/leaflet/marker-icon.png`,
        iconRetinaUrl: `assets/leaflet/marker-icon-2x.png`,
        shadowUrl: `assets/leaflet/marker-shadow.png`,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [0, -40]
      })
    }).addTo(this.map);

    this.map.on('move', () => {
      this.marker.setLatLng(this.map.getCenter()); // Keep the marker on the map's center
    });
  }

  async submit() {
    if (this.floors != null && !(this.floors >= 1 && this.floors <= 60)) {
      this.error = this.translator.instant('WIRELESS_FLOORS_REQUIRED');
      return;
    }
    const legal = this.contact.person_type_fa == 'حقوقی'; // a lead for a legal person needs its national id
    if (!this.linkToken && (this.contact.name.trim().length < 2 || !/^(\+98|0098|0)?9\d{9}$/.test(this.contact.mobile.replace(/[\s-]/g, '')) || (legal && !this.contact.identification_no.trim()))) {
      this.error = this.translator.instant('DEDICATE_LOCATION_CONTACT_REQUIRED');
      return;
    }
    const center = this.map.getCenter();
    const body = {
      latitude: center.lat,
      longitude: center.lng,
      floors: this.floors,
      address: this.address,
      ...(this.linkToken ? { token: this.linkToken } : this.contact)
    };
    this.error = null;
    this.loading = true;
    try {
      await this.service.apiCall('api/dedicate_location/submit', body).toPromise();
      this.done = true;
    } catch (error) {
      this.error = this.translator.instant(error.status == 429 ? 'DEDICATE_LOCATION_TOO_MANY' : error.status == 400 ? 'DEDICATE_LOCATION_CONTACT_REQUIRED' : 'DEDICATE_LOCATION_FAILED');
      if (error.status == 400) {
        this.linkToken = null; // the link was used up meanwhile, so the contact is needed after all
      }
    }
    this.loading = false;
  }

}
